import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Download, UploadCloud, Loader2, AlertCircle, Trash2 } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL; // Your backend URL

function MetadataManager({character, apiKey}) {
    const selectedCharacter = character
    const [csvFiles, setCsvFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [uploadStatus, setUploadStatus] = useState({ msg: '', type: '' });
    
    const fileInputRef = useRef(null);

    const fetchCsvList = async () => {
        setIsLoading(true);
        setError('');
        setUploadStatus({ msg: '', type: ''});
        try {
            const response = await axios.get(`${API_BASE_URL}/api/admin/files/list/metadata/${selectedCharacter}`, {
                headers: { 'X-API-KEY': apiKey }
            });
            setCsvFiles(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch CSV list.');
            setCsvFiles([]); // Clear list on error
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch CSV list when character or API key changes
    useEffect(() => {
        if (!selectedCharacter || !apiKey) {
            setCsvFiles([]);
            return;
        }

        const debounceTimer = setTimeout(() => {
            fetchCsvList();
        }, 500); // Debounce to prevent API calls on every key press

        return () => clearTimeout(debounceTimer);

    }, [selectedCharacter, apiKey]);

    const handleDownload = async (filename) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/files/metadata/${selectedCharacter}/${filename}`, {
                headers: { 'X-API-KEY': apiKey },
            });
            const url = response.data.url;
            console.log({response})
            window.open(url, '_blank');
        } catch (err) {
            setError(err.response?.data?.message || 'Download failed.');
        }
    };

    const handleFileSelectAndUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('character', selectedCharacter);
        formData.append('type', "metadata");

        setIsLoading(true);
        setUploadStatus({ msg: `Uploading ${file.name}...`, type: 'info' });

        try {
            await axios.post(`${API_BASE_URL}/api/admin/files/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-API-KEY': apiKey,
                },
            });
            setUploadStatus({ msg: 'File uploaded successfully! Refreshing list...', type: 'success' });
            
            // Refetch data after upload
            fetchCsvList()

        } catch (err) {
            setUploadStatus({ msg: err.response?.data?.message || 'Upload failed.', type: 'error' });
        } finally {
            setIsLoading(false);
            // Reset file input value
            if(fileInputRef.current) {
              fileInputRef.current.value = '';
            }
        }
    };

    const handleDelete = async (filename) => {
        // Critical UX: Always ask for confirmation before deleting
        if (!window.confirm(`Are you sure you want to delete the file: ${filename}? This action cannot be undone.`)) {
            return;
        }

        try {
            const url = `${API_BASE_URL}/api/admin/files/delete`;
            const payload = {
                character: selectedCharacter,
                filename: filename,
                type: "metadata"
            };

            // Use axios.post with the payload and headers
            await axios.post(url, payload, {
                headers: { 'X-API-KEY': apiKey }
            });
            
            // Success: Remove the deleted file from the local state for an instant UI update
            setCsvFiles(prevFiles => prevFiles.filter(f => f.filename !== filename));
            setUploadStatus({ msg: `Successfully deleted ${filename}`, type: 'success' });

            // Refetch data after delete
            fetchCsvList()

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete file.');
        }
    };

    return (
        <div className="bg-gray-100 min-h-screen antialiased">
            <div className="container mx-auto px-4 py-8">
                <main className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6 sm:p-8">
                    {/* Section 2: Manage Metadata */}
                    <section>
                         <div className="flex justify-between items-center mb-4">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-700">Manage Metadata</h2>
                                <p className="text-sm text-gray-500">View, download, or upload new CSV files.</p>
                            </div>
                            <label className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white font-semibold text-sm rounded-lg shadow-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                                <UploadCloud className="h-5 w-5 mr-2" />
                                Upload New CSV
                                <input 
                                    type="file" 
                                    className="hidden" 
                                    accept=".csv"
                                    ref={fileInputRef}
                                    onChange={handleFileSelectAndUpload}
                                    disabled={!apiKey || !selectedCharacter}
                                />
                            </label>
                        </div>
                        
                        {/* Status/Error Messages */}
                        {uploadStatus.msg && (
                            <div className={`p-4 mt-4 rounded-md text-sm ${
                                uploadStatus.type === 'success' ? 'bg-green-100 text-green-800' :
                                uploadStatus.type === 'error' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'
                            }`}
                            >
                                {uploadStatus.msg}
                            </div>
                        )}
                         {error && (
                            <div className="p-4 mt-4 rounded-md text-sm bg-red-100 text-red-800 flex items-center">
                                <AlertCircle className="h-5 w-5 mr-3" />
                                {error}
                            </div>
                        )}

                        {/* Files Table */}
                        <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
                                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {isLoading ? (
                                            <tr>
                                                <td colSpan="4" className="text-center px-6 py-12">
                                                    <div className="flex justify-center items-center text-gray-500">
                                                        <Loader2 className="h-6 w-6 animate-spin mr-3" />
                                                        Loading files...
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : csvFiles.length > 0 ? (
                                            csvFiles.map((file, index) => (
                                                <tr key={file.filename} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{file.filename}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                        {(index == 0) && (
                                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                                Latest
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <div className="flex items-center justify-end space-x-4">
                                                            <button 
                                                                onClick={() => handleDownload(file.filename)}
                                                                className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-900"
                                                            >
                                                                <Download className="h-4 w-4" /> Download
                                                            </button>
                                                            <button 
                                                                onClick={() => handleDelete(file.filename)}
                                                                className="inline-flex items-center gap-2 text-red-600 hover:text-red-900"
                                                            >
                                                                <Trash2 className="h-4 w-4" /> Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                             <tr>
                                                <td colSpan="4" className="text-center px-6 py-12">
                                                    <div className="text-gray-500">
                                                        <p className="font-semibold">No files found.</p>
                                                        {/* <p className="text-sm mt-1">Please enter a valid API Key to see files or upload a new one.</p> */}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}

export default MetadataManager;